import React, { useState, useEffect } from 'react';
import { WEEKDAY_LABELS, FREQUENCY_LABELS } from '../types/recurrence';
import type { RecurrenceFrequency, CreateRecurrenceSeriesRequest } from '../types/recurrence';
import { useRecurrence } from '../hooks/useRecurrence';
import { useI18n } from '../contexts/I18nContext';

interface RecurrenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: {
    propertyId?: string;
    employeeId?: string;
    date?: Date;
  };
}

const RecurrenceModal: React.FC<RecurrenceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialData
}) => {
  const { t } = useI18n();
  const { createSeries, validateConflicts, loading } = useRecurrence();
  
  const [formData, setFormData] = useState<CreateRecurrenceSeriesRequest>({
    property_id: initialData?.propertyId || '',
    employee_id: initialData?.employeeId || '',
    frequency: 'weekly',
    interval: 1,
    start_date: initialData?.date?.toISOString().split('T')[0] || '',
    scheduled_time: '09:00',
    duration: 120,
    reminder_minutes: 30,
    days_of_week: [1, 3, 5] // Segunda, Quarta, Sexta por padrão
  });

  const [conflicts, setConflicts] = useState<string[]>([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [endOption, setEndOption] = useState<'date' | 'count'>('date');

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        property_id: initialData.propertyId || prev.property_id,
        employee_id: initialData.employeeId || prev.employee_id,
        start_date: initialData.date?.toISOString().split('T')[0] || prev.start_date
      }));
    }
  }, [initialData]);

  const handleFrequencyChange = (frequency: RecurrenceFrequency) => {
    const updates: Partial<CreateRecurrenceSeriesRequest> = { frequency };
    
    // Configurar campos padrão baseado na frequência
    if (frequency === 'monthly') {
      updates.day_of_month = 1;
      updates.days_of_week = undefined;
      updates.interval = 1; // Mensal = intervalo 1
    } else if (frequency === 'weekly') {
      updates.days_of_week = [1]; // Segunda-feira
      updates.day_of_month = undefined;
      updates.interval = 1; // Semanal = intervalo 1
    } else if (frequency === 'biweekly') {
      updates.days_of_week = [1]; // Segunda-feira
      updates.day_of_month = undefined;
      updates.interval = 1; // Quinzenal sempre usa intervalo 1 (a cada 2 semanas)
    } else if (frequency === 'daily') {
      updates.days_of_week = undefined;
      updates.day_of_month = undefined;
      updates.interval = 1; // Diário = intervalo 1
    }

    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleDayOfWeekToggle = (day: number) => {
    setFormData(prev => {
      const current = prev.days_of_week || [];
      const newDays = current.includes(day)
        ? current.filter(d => d !== day)
        : [...current, day].sort();
      
      return { ...prev, days_of_week: newDays };
    });
  };

  const handleValidate = async () => {
    const validation = await validateConflicts(formData);
    if (validation) {
      setConflicts(validation.conflicts);
      setShowConflicts(validation.has_conflicts);
      
      if (!validation.has_conflicts) {
        alert(t('recurrence.noConflicts'));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Preparar dados finais
      const submitData = {
        ...formData,
        end_date: endOption === 'date' ? formData.end_date : undefined,
        occurrences_count: endOption === 'count' ? formData.occurrences_count : undefined
      };

      await createSeries(submitData);
      alert(t('recurrence.successCreated'));
      onSuccess();
      onClose();
    } catch (error: any) {
      if (error.conflicts) {
        setConflicts(error.conflicts);
        setShowConflicts(true);
      } else {
        alert(error.message || 'Erro ao criar série recorrente');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content recurrence-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('recurrence.createTitle')}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="recurrence-form">
          {/* Frequência */}
          <div className="form-group">
            <label>{t('recurrence.frequencyRequired')}</label>
            <select
              value={formData.frequency}
              onChange={e => handleFrequencyChange(e.target.value as RecurrenceFrequency)}
              required
            >
              {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Intervalo - apenas para frequências que permitem customização */}
          {formData.frequency !== 'biweekly' && (
          <div className="form-group">
            <label>
                {formData.frequency === 'daily' && 'Repetir a cada quantos dias?'}
                {formData.frequency === 'weekly' && 'Repetir a cada quantas semanas?'}
                {formData.frequency === 'monthly' && 'Repetir a cada quantos meses?'}
            </label>
            <input
              type="number"
              min="1"
                max="365"
              value={formData.interval}
                onChange={e => {
                  const value = parseInt(e.target.value);
                  if (value > 0) {
                    setFormData({ ...formData, interval: value });
                  }
                }}
              required
            />
              <small className="form-help">
                {formData.frequency === 'daily' && (
                  <>
                    <strong>1</strong> = Diário (todos os dias)<br/>
                    <strong>2</strong> = A cada 2 dias<br/>
                    <strong>3</strong> = A cada 3 dias<br/>
                    <strong>15</strong> = A cada 15 dias
                  </>
                )}
                {formData.frequency === 'weekly' && (
                  <>
                    <strong>1</strong> = Semanal (toda semana)<br/>
                    <strong>2</strong> = A cada 2 semanas<br/>
                    <strong>3</strong> = A cada 3 semanas<br/>
                    <strong>4</strong> = Mensalmente (a cada 4 semanas)
                  </>
                )}
                {formData.frequency === 'monthly' && (
                  <>
                    <strong>1</strong> = Mensal (todo mês)<br/>
                    <strong>2</strong> = Bimestral (a cada 2 meses)<br/>
                    <strong>3</strong> = Trimestral (a cada 3 meses)<br/>
                    <strong>6</strong> = Semestral (a cada 6 meses)
                  </>
                )}
              </small>
            </div>
          )}
          
          {/* Para biweekly, mostrar informação fixa */}
          {formData.frequency === 'biweekly' && (
            <div className="form-group">
              <label>{t('recurrence.frequency')}</label>
              <div className="info-box" style={{ padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                <strong>Quinzenal:</strong> A cada 2 semanas (14 dias)
              </div>
          </div>
          )}

          {/* Dias da semana (para weekly/biweekly) */}
          {(formData.frequency === 'weekly' || formData.frequency === 'biweekly') && (
            <div className="form-group">
              <label>Dias da semana *</label>
              <div className="weekday-selector">
                {Object.entries(WEEKDAY_LABELS).map(([day, label]) => {
                  const dayNum = parseInt(day);
                  const isSelected = formData.days_of_week?.includes(dayNum);
                  
                  return (
                    <button
                      key={day}
                      type="button"
                      className={`weekday-button ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleDayOfWeekToggle(dayNum)}
                    >
                      {label.substring(0, 3)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Dia do mês (para monthly) */}
          {formData.frequency === 'monthly' && (
            <div className="form-group">
              <label>{t('recurrence.dayOfMonth')}</label>
              <input
                type="number"
                min="1"
                max="31"
                value={formData.day_of_month || 1}
                onChange={e => setFormData({ ...formData, day_of_month: parseInt(e.target.value) })}
                required
              />
            </div>
          )}

          {/* Data de início */}
          <div className="form-group">
            <label>{t('recurrence.startDate')}</label>
            <input
              type="date"
              value={formData.start_date}
              onChange={e => setFormData({ ...formData, start_date: e.target.value })}
              required
            />
          </div>

          {/* Opções de término */}
          <div className="form-group">
            <label>Termina</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  value="date"
                  checked={endOption === 'date'}
                  onChange={() => setEndOption('date')}
                />
                Em data específica
              </label>
              <label>
                <input
                  type="radio"
                  value="count"
                  checked={endOption === 'count'}
                  onChange={() => setEndOption('count')}
                />
                Após número de ocorrências
              </label>
            </div>

            {endOption === 'date' && (
              <input
                type="date"
                value={formData.end_date || ''}
                onChange={e => setFormData({ ...formData, end_date: e.target.value })}
                min={formData.start_date}
              />
            )}

            {endOption === 'count' && (
              <input
                type="number"
                min="1"
                max="365"
                placeholder="Número de ocorrências"
                value={formData.occurrences_count || ''}
                onChange={e => setFormData({ 
                  ...formData, 
                  occurrences_count: parseInt(e.target.value) || undefined 
                })}
              />
            )}
          </div>

          {/* Horário */}
          <div className="form-group">
            <label>Horário *</label>
            <input
              type="time"
              value={formData.scheduled_time}
              onChange={e => setFormData({ ...formData, scheduled_time: e.target.value })}
              required
            />
          </div>

          {/* Duração */}
          <div className="form-group">
            <label>{t('recurrence.durationRequired')}</label>
            <input
              type="number"
              min="15"
              step="15"
              value={formData.duration}
              onChange={e => setFormData({ ...formData, duration: parseInt(e.target.value) })}
              required
            />
          </div>

          {/* Preço */}
          <div className="form-group">
            <label>Preço (R$)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={formData.price || ''}
              onChange={e => setFormData({ 
                ...formData, 
                price: parseFloat(e.target.value) || undefined 
              })}
            />
          </div>

          {/* Instruções especiais */}
          <div className="form-group">
            <label>Instruções especiais</label>
            <textarea
              rows={3}
              value={formData.special_instructions || ''}
              onChange={e => setFormData({ ...formData, special_instructions: e.target.value })}
              placeholder="Observações sobre o agendamento..."
            />
          </div>

          {/* Lembrete */}
          <div className="form-group">
            <label>Lembrar antes (minutos)</label>
            <select
              value={formData.reminder_minutes}
              onChange={e => setFormData({ ...formData, reminder_minutes: parseInt(e.target.value) })}
            >
              <option value="0">Sem lembrete</option>
              <option value="15">15 minutos</option>
              <option value="30">30 minutos</option>
              <option value="60">1 hora</option>
              <option value="120">2 horas</option>
              <option value="1440">1 dia</option>
            </select>
          </div>

          {/* Alertas de conflito */}
          {showConflicts && conflicts.length > 0 && (
            <div className="alert alert-warning">
              <h4>⚠️ Conflitos detectados</h4>
              <p>Os seguintes horários já possuem agendamentos:</p>
              <ul>
                {conflicts.slice(0, 5).map((conflict, idx) => (
                  <li key={idx}>
                    {new Date(conflict).toLocaleString('pt-BR')}
                  </li>
                ))}
                {conflicts.length > 5 && <li>... e mais {conflicts.length - 5}</li>}
              </ul>
            </div>
          )}

          {/* Botões */}
          <div className="modal-actions">
            <button
              type="button"
              onClick={handleValidate}
              className="btn btn-secondary"
              disabled={loading}
            >
              🔍 Validar Conflitos
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">{t('actions.cancel')}</button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading || (showConflicts && conflicts.length > 0)}
            >
              {loading ? 'Criando...' : 'Criar Série'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecurrenceModal;

